<aside
  class="estimated-costs-panel"
  data-testid="estimated-costs-panel"
  [attr.aria-busy]="showSkeleton() || pending() ? 'true' : null"
>
  @if (showSkeleton()) {
    <app-estimated-costs-skeleton />
  } @else if (!pending()) {
    <puro-typography component="h2" variant="h4" class="estimated-costs-panel__title">
      {{ 'createShipment.serviceStep.deliveryService.breakdown.title' | translate }}
    </puro-typography>

    @if (breakdown(); as view) {
      <div class="estimated-costs-panel__row">
        <span class="estimated-costs-panel__label">
          <puro-typography component="span" variant="body2">{{
            'createShipment.serviceStep.deliveryService.breakdown.baseCost' | translate
          }}</puro-typography>
        </span>
        @if (view.discountedBaseCost) {
          <puro-typography
            component="span"
            variant="body2"
            class="estimated-costs-panel__amount estimated-costs-panel__amount--list"
          >
            <s>{{ formatCurrencyAmount(view.baseCost) }}</s>
          </puro-typography>
          @if (view.discountRate !== undefined) {
            <puro-typography
              component="span"
              variant="body2"
              class="estimated-costs-panel__discount"
            >
              {{
                'createShipment.serviceStep.deliveryService.breakdown.discountPayOnly'
                  | translate: discountParams(view.discountRate)
              }}
            </puro-typography>
          }
          <puro-typography
            component="span"
            variant="body2"
            class="estimated-costs-panel__amount estimated-costs-panel__amount--pay"
          >
            <strong>{{ formatCurrencyAmount(view.discountedBaseCost) }}</strong>
          </puro-typography>
        } @else {
          <puro-typography component="span" variant="body2" class="estimated-costs-panel__amount">
            {{ formatCurrencyAmount(view.baseCost) }}
          </puro-typography>
        }
      </div>

      @if (view.additionalSurcharges.length > 0) {
        <div class="estimated-costs-panel__surcharges" data-testid="additional-surcharges">
          <puro-typography
            component="p"
            variant="body2"
            class="estimated-costs-panel__surcharges-title"
          >
            {{
              'createShipment.serviceStep.deliveryService.breakdown.additionalSurcharges'
                | translate
            }}
          </puro-typography>
          @for (line of view.additionalSurcharges; track line.code + line.listAmount) {
            <div class="estimated-costs-panel__row">
              <span class="estimated-costs-panel__label">
                <puro-typography component="span" variant="body2">{{
                  surchargeLabelKey(line.labelCode, line.displayMode) | translate
                }}</puro-typography>
                @if (line.descriptionKey) {
                  <puro-typography
                    component="span"
                    variant="body2"
                    class="estimated-costs-panel__description"
                    >{{ line.descriptionKey | translate: line.descriptionParams }}</puro-typography
                  >
                } @else if (line.description) {
                  <puro-typography
                    component="span"
                    variant="body2"
                    class="estimated-costs-panel__description"
                    >{{ line.description }}</puro-typography
                  >
                }
              </span>
              @if (line.displayMode === 'included') {
                <puro-typography
                  component="span"
                  variant="body2"
                  class="estimated-costs-panel__amount"
                >
                  {{ 'createShipment.serviceStep.deliveryService.breakdown.included' | translate }}
                </puro-typography>
              } @else if (line.discountedAmount) {
                <puro-typography
                  component="span"
                  variant="body2"
                  class="estimated-costs-panel__amount estimated-costs-panel__amount--list"
                >
                  <s>{{ formatCurrencyAmount(line.listAmount) }}</s>
                </puro-typography>
                @if (view.discountRate !== undefined) {
                  <puro-typography
                    component="span"
                    variant="body2"
                    class="estimated-costs-panel__discount"
                  >
                    {{
                      'createShipment.serviceStep.deliveryService.breakdown.discountPayOnly'
                        | translate: discountParams(view.discountRate)
                    }}
                  </puro-typography>
                }
                <puro-typography
                  component="span"
                  variant="body2"
                  class="estimated-costs-panel__amount estimated-costs-panel__amount--pay"
                >
                  <strong>{{ formatCurrencyAmount(line.discountedAmount) }}</strong>
                </puro-typography>
              } @else {
                <puro-typography
                  component="span"
                  variant="body2"
                  class="estimated-costs-panel__amount"
                >
                  {{ formatCurrencyAmount(line.listAmount) }}
                </puro-typography>
              }
            </div>
          }
        </div>
      }

      <div class="estimated-costs-panel__row estimated-costs-panel__row--section">
        <span class="estimated-costs-panel__label">
          <puro-typography component="span" variant="body2">{{
            'createShipment.serviceStep.deliveryService.breakdown.fuelSurcharge' | translate
          }}</puro-typography>
        </span>
        @if (view.discountedFuelSurcharge) {
          <puro-typography
            component="span"
            variant="body2"
            class="estimated-costs-panel__amount estimated-costs-panel__amount--list"
          >
            <s>{{ formatCurrencyAmount(view.fuelSurcharge) }}</s>
          </puro-typography>
          @if (view.discountRate !== undefined) {
            <puro-typography
              component="span"
              variant="body2"
              class="estimated-costs-panel__discount"
            >
              {{
                'createShipment.serviceStep.deliveryService.breakdown.discountPayOnly'
                  | translate: discountParams(view.discountRate)
              }}
            </puro-typography>
          }
          <puro-typography
            component="span"
            variant="body2"
            class="estimated-costs-panel__amount estimated-costs-panel__amount--pay"
          >
            <strong>{{ formatCurrencyAmount(view.discountedFuelSurcharge) }}</strong>
          </puro-typography>
        } @else {
          <puro-typography component="span" variant="body2" class="estimated-costs-panel__amount">
            {{ formatCurrencyAmount(view.fuelSurcharge) }}
          </puro-typography>
        }
      </div>
      <div class="estimated-costs-panel__row estimated-costs-panel__row--bordered">
        <span class="estimated-costs-panel__label">
          <puro-typography component="span" variant="body2">{{
            'createShipment.serviceStep.deliveryService.breakdown.gstHst' | translate
          }}</puro-typography>
        </span>
        @if (view.discountedGstHst) {
          <puro-typography
            component="span"
            variant="body2"
            class="estimated-costs-panel__amount estimated-costs-panel__amount--list"
          >
            <s>{{ formatCurrencyAmount(view.gstHst) }}</s>
          </puro-typography>
          @if (view.discountRate !== undefined) {
            <puro-typography
              component="span"
              variant="body2"
              class="estimated-costs-panel__discount"
            >
              {{
                'createShipment.serviceStep.deliveryService.breakdown.discountPayOnly'
                  | translate: discountParams(view.discountRate)
              }}
            </puro-typography>
          }
          <puro-typography
            component="span"
            variant="body2"
            class="estimated-costs-panel__amount estimated-costs-panel__amount--pay"
          >
            <strong>{{ formatCurrencyAmount(view.discountedGstHst) }}</strong>
          </puro-typography>
        } @else {
          <puro-typography component="span" variant="body2" class="estimated-costs-panel__amount">
            {{ formatCurrencyAmount(view.gstHst) }}
          </puro-typography>
        }
      </div>
      <div class="estimated-costs-panel__total">
        <puro-typography component="span" variant="body1">{{
          'createShipment.serviceStep.deliveryService.breakdown.total' | translate
        }}</puro-typography>
        <div class="estimated-costs-panel__total-amount">
          @if (view.originalTotal) {
            <puro-typography
              component="span"
              variant="body1"
              class="estimated-costs-panel__amount estimated-costs-panel__amount--list"
            >
              <s>{{ formatCurrencyAmount(view.originalTotal) }}</s>
            </puro-typography>
          }
          <puro-typography component="span" variant="body1" class="estimated-costs-panel__amount">
            <strong>{{ formatCurrencyAmount(view.total) }}</strong>
          </puro-typography>
        </div>
      </div>
    }
  }
</aside>
